import { ReactNode, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentPerson } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { AppointmentType, Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { PersonTypes } from '@frontend/api-person';
import { ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { InfinitePaginatedList } from '@frontend/components';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Chip,
  DatePickerField,
  DropdownField,
  FormFieldActionTypes,
  NakedButton,
  SpinningLoader,
  Text,
  TimeField,
  Tray,
  useAlert,
  useDebouncedValue,
  useForm,
  SearchField,
  useFormField,
} from '@frontend/design-system';
import { ColorPickerModal } from '../../../../components/ColorPicker/ColorPickerModal';
import { useDropdownListSelector } from '../../../../components/DropdownListSelector';
import { ListBoxOption } from '../../../../components/list-box-popover/list-box-option.js';
import { MultiViewCombobox } from '../../../../components/multi-view-combobox';
import { AppointmentStatusV3Enum } from '../../../../components/v3/EventCardAppointmentStatusChip/types';
import { EVENT_BACKGROUND_COLOR, EVENT_BACKGROUND_COLOR_PALETTE } from '../../../../constants';
import {
  useCalendarEventsConfigurationShallowStore,
  useCalendarEventsFormTypeShallowStore,
  useGetAppointmentColorsEnabled,
  useLocationTimezone,
  useSchedulingLocationInfo,
} from '../../../../hooks';
import { usePastDateBanner } from '../../../../hooks/';
import { getLabelWithTimezoneAbbr } from '../../../../utils';
import { formatDateTime, getDateString, getTimeDiff, getTodaysDate } from '../../../../utils/date-helpers';
import { ProviderDropdownListSelector } from '../../components/ProviderDropdownListSelector';
import { isUUID } from '../../utils';
import { CustomContactForm } from './appointment/custom-contact-form';
import { usePatientSelector } from './appointment/use-patient-selector';
import { calendarEventsTrackingIds } from './calendar-events-tracking-ids';
import { DeleteAppointmentButton, DeleteAppointmentButtonV3 } from './delete-appointment-button';
import { RecommendedDurationBanner } from './recommended-duration-banner';
import {
  AppointmentAvailabilityFormValues,
  OnCreateCalendarEvent,
  OnDeleteCalendarEvent,
  OnUpdateCalendarEvent,
} from './types';

dayjs.extend(utc);

export const CACHE_AND_STALE_TIME = 1000 * 60 * 5; // 5 minute

const PROVIDER_SELECTOR_MOBILE_WIDTH = 176;
const PROVIDER_SELECTOR_DESKTOP_WIDTH = 220;

const MIN_APPT_DURATION = 30; // 30 minutes

export type AppointmentTypeExtended = AppointmentType & { active?: boolean };

interface NewAppointmentFormProps {
  selectedLocationIds: string[];
  isScheduleV3: boolean;
  isStatusDropdownVisible?: boolean;
  appointmentTypes: AppointmentType[];
  providersList: Provider[];
  availableAppointmentTimes: ScheduleTypes.OpeningBlock[];
  isAvailableAppointmentTimesLoading: boolean;
  selectedLocationId: string;
  closeModal: () => void;
  refetchAppointmentTypes: () => void;
  setSelectedFormValuesForAvailabilityApi: (
    appointmentAvailabilityFormValues: AppointmentAvailabilityFormValues
  ) => void;
  onCreateAppointment: (payload: OnCreateCalendarEvent, apptStatus?: EventStatus) => void;
  onDeleteAppointment?: ({ id, createdByLocationId }: OnDeleteCalendarEvent) => void; // for v3, we handle deletion from button component itself
  onUpdateAppointment: (payload: OnUpdateCalendarEvent, apptStatus?: EventStatus) => void;
  refetchAppointments?: (locationId?: string) => void | Promise<void>;
  setSelectedLocationId: (locationId: string) => void;
}

export const NewAppointmentForm = ({
  selectedLocationIds,
  isScheduleV3,
  isStatusDropdownVisible,
  appointmentTypes,
  providersList,
  availableAppointmentTimes,
  isAvailableAppointmentTimesLoading,
  selectedLocationId,
  closeModal,
  refetchAppointmentTypes,
  onDeleteAppointment,
  onUpdateAppointment,
  onCreateAppointment,
  refetchAppointments,
  setSelectedFormValuesForAvailabilityApi,
  setSelectedLocationId,
}: NewAppointmentFormProps) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });
  const { t } = useTranslation('scheduleCalendarEvents');
  const alerts = useAlert();

  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();

  const [customDuration, setCustomDuration] = useState<number>(0);

  const { locationTimezoneAbbr } = useLocationTimezone(selectedLocationId);
  const { getScopeName, formatLocationTime } = useSchedulingLocationInfo();

  const { hasWSM, wsmActiveLocationIds } = SchedulerV3.Helpers.useCheckHasWSM(selectedLocationId);
  const { invalidateListCalendarEvents } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const { configuredCalendarEvent, setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore(
    'configuredCalendarEvent',
    'setConfiguredCalendarEvent'
  );

  const isEditMode = !!configuredCalendarEvent?.eventId;
  const { selectedPerson, setSelectedPerson } = useCalendarEventsFormTypeShallowStore(
    'selectedPerson',
    'setSelectedPerson'
  );

  const [shouldShowFormErrors, setShouldShowFormErrors] = useState(false);
  const [isSavingAppointment, setIsSavingAppointment] = useState<boolean>(false);

  const [singleSourceId, setSingleSourceId] = useState<string>('');

  const useSelectedPerson = isScheduleV3 && selectedPerson !== null;

  const appointmentPerson = useMemo(() => {
    if (useSelectedPerson) {
      return {
        firstName: selectedPerson.FirstName,
        lastName: selectedPerson.LastName,
        personId: selectedPerson.PersonID,
        email: selectedPerson.Email,
        mobilePhone: selectedPerson.MobilePhone,
        homePhone: selectedPerson.HomePhone,
        sourceId: selectedPerson.SourceID,
        preferredName: selectedPerson.PreferredName ?? '',
        pmId: selectedPerson.PMID ?? '',
        pmDisplayId: selectedPerson.PMDisplayID ?? '',
      };
    }
    return {};
  }, [selectedPerson, useSelectedPerson]);

  const [personValue, setPersonValue] = useState<AppointmentPerson>(appointmentPerson ?? {});

  const providerDropdownListSelectorProps = useDropdownListSelector({
    value: configuredCalendarEvent?.providerId ?? '',
  });

  const {
    isIntegratedOffice,
    data: integrations,
    isLoading: isLoadingIntegrationDetails,
  } = DataSourcesQueries.useGetAccessibleIntegrations();

  const shouldRenderSourceDropdown = isIntegratedOffice && (integrations ?? []).length > 1;

  const updateEndTime = (startTime: string, appointmentTypeValue = '') => {
    const appointmentTypeDuration =
      appointmentTypes?.find((type) => type.id === appointmentTypeValue)?.durationMinutes ?? 0;

    const duration = customDuration || appointmentTypeDuration || MIN_APPT_DURATION;

    return dayjs(startTime, 'HH:mm:ss').add(duration, 'minute');
  };

  const {
    getFieldProps,
    values,
    seedValues,
    reset: resetAppointmentForm,
    isComplete: isAppointmentFormComplete,
    validate: validateAppointmentForm,
  } = useForm({
    fields: {
      locationId: {
        type: 'dropdown',
        value: configuredCalendarEvent?.locationId || selectedLocationIds?.[0] || '',
        required: true,
        validator: ({ value }) =>
          !wsmActiveLocationIds.includes(value)
            ? t('Please select a location that has Appointment Creation access')
            : '',
      },
      source: {
        type: 'dropdown',
        value: configuredCalendarEvent?.createdBySourceId,
        required: shouldRenderSourceDropdown,
      },
      appointmentStatus: {
        type: 'dropdown',
        value: configuredCalendarEvent?.apptStatusV3 || AppointmentStatusV3Enum.UNCONFIRMED,
        required: true,
      },
      appointmentType: {
        type: 'dropdown',
        value: '',
        required: true,
      },
      backgroundColor: {
        type: 'text',
        value: configuredCalendarEvent?.backgroundColor || EVENT_BACKGROUND_COLOR,
        hidden: !canUseAppointmentColors,
      },
      appointmentDate: {
        type: 'datePicker',
        value: !!configuredCalendarEvent?.eventId
          ? configuredCalendarEvent.startDate
          : getDateString(configuredCalendarEvent?.calendarDateValue) || getTodaysDate(),
        required: true,
      },
      appointmentStartTime: {
        type: 'time',
        value:
          formatDateTime(configuredCalendarEvent?.startHour, {
            inputFormat: 'hh:mm A',
            outputFormat: 'h:mma',
          }) ?? '',
        required: true,
      },
      appointmentEndTime: {
        type: 'time',
        value:
          formatDateTime(configuredCalendarEvent?.endHour, {
            inputFormat: 'hh:mm A',
            outputFormat: 'h:mma',
          }) ?? '',
        required: true,
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type !== FormFieldActionTypes.Update) {
        return state;
      }

      if (action.payload.name === 'appointmentEndTime') {
        // Set the custom duration if the end time is changed
        if (state.appointmentStartTime && state.appointmentEndTime) {
          const duration = getTimeDiff(state.appointmentStartTime.value, state.appointmentEndTime.value, {
            crossDay: true,
          });

          setCustomDuration(duration);
        }
      } else if (action.payload.name === 'appointmentStartTime' || action.payload.name === 'appointmentType') {
        if (!state.appointmentStartTime.value) return state;

        const endTime = updateEndTime(state.appointmentStartTime.value, state.appointmentType.value);

        return {
          ...state,
          appointmentEndTime: {
            ...state.appointmentEndTime,
            error: '',
            value: endTime.format('HH:mm:ss'),
            displayValue: endTime.format('h:mma'),
          },
        };
      }
      return state;
    },
  });

  const searchFieldProps = useFormField({ type: 'text', value: '' });

  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value, 300);

  const filteredAppointmentTypes = useMemo(() => {
    const sortedApptTypes = [...(appointmentTypes ?? [])].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
    if (!debouncedSearchValue) {
      return sortedApptTypes;
    }
    return sortedApptTypes.filter((appointmentType) =>
      appointmentType.name?.toLowerCase().includes(debouncedSearchValue.toLowerCase())
    );
  }, [appointmentTypes, debouncedSearchValue]);

  useEffect(() => {
    if (values.locationId) {
      setSelectedLocationId(values.locationId);
    }
  }, [values.locationId]);

  // Set the source if the location has a single source or is non integrated
  useEffect(() => {
    const isNonIntegratedOrSingleSource =
      integrations && integrations.length > 0 && !isScheduleV3 && (integrations.length === 1 || !isIntegratedOffice);

    if (isNonIntegratedOrSingleSource) {
      setSingleSourceId(integrations[0].SourceID);
      seedValues({
        ...values,
        source: integrations[0].SourceID,
      });
    }

    // TODO: Add logic to set the source tenant id for v3
  }, [integrations, isScheduleV3]);

  // Manage patient selection and creation
  const {
    infinitePersonQuery,
    isCreatingContact,
    isCreateContactDisabled,
    patientForm,
    selectedPatientOption,
    onSearchChange,
    onPrimaryAction,
    onPatientChange,
    onPatientRemove,
    setSelectedPatientOption,
  } = usePatientSelector({
    isIntegratedOffice,
    isScheduleV3,
    locationId: selectedLocationId,
    setPersonValue,
  });

  const primaryViewConfig = {
    title: t('Add New Contact'),
    primaryLabel: t('Save Contact'),
    primaryButtonTrackingId: calendarEventsTrackingIds.createNewContactButton,
    cancelButtonTrackingId: calendarEventsTrackingIds.cancelNewContactButton,
    content: <CustomContactForm form={patientForm} locationId={selectedLocationId} />,
    isLoading: isCreatingContact,
    isDisabled: isCreateContactDisabled,
    onAction: onPrimaryAction,
    onCancel: () => {},
  };

  const defaultViewConfig = {
    primaryButtonLabel: t('Add New Contact'),
    primaryButtonTrackingId: calendarEventsTrackingIds.addNewContactButton,
    cancelButtonTrackingId: calendarEventsTrackingIds.cancelContactButton,
    content: (
      <InfinitePaginatedList
        height={360}
        infiniteQueryProps={infinitePersonQuery}
        renderListItem={({ listItem }): ReactNode => {
          const { PersonID, Status } = listItem;

          // Parse birthdate in UTC and normalize to start of day to prevent timezone-related date shifts
          const birthdayObj = dayjs.utc(listItem.Birthdate).startOf('day');
          const birthdate = birthdayObj.isValid() ? birthdayObj.format('MM/DD/YYYY') : '-';

          return (
            <ListBoxOption key={PersonID} value={PersonID} onClick={() => selectPerson(listItem)}>
              <Text>{getFullName(listItem)}</Text>
              <Text size='small' color='light'>
                {Status !== 'Active' ? (
                  <Chip variant={Status === 'Prospective' ? 'rose' : 'neutral'}>{Status}</Chip>
                ) : (
                  birthdate
                )}
              </Text>
            </ListBoxOption>
          );
        }}
      />
    ),
    onAction: isIntegratedOffice || !isScheduleV3 ? undefined : () => {}, // show action button only for v3 core
  };

  useEffect(() => {
    const appointmentTypeId = appointmentTypes?.find(
      (type) => type?.name?.toLowerCase() === configuredCalendarEvent?.appointmentType?.toLowerCase()
    )?.id;

    if (!appointmentTypeId) {
      return;
    }

    seedValues({
      appointmentType: appointmentTypeId,
    });
  }, [appointmentTypes, configuredCalendarEvent?.appointmentType]);

  const clearAppointmentForm = ({ shouldClearSourceId = true }) => {
    resetAppointmentForm();
    refetchAppointmentTypes();
    providerDropdownListSelectorProps.onChange('');
    setSelectedPatientOption(undefined);
    setSelectedPerson(null);
    setConfiguredCalendarEvent(null);
    shouldClearSourceId && setSingleSourceId('');
  };

  const cancelButtonHandler = () => {
    clearAppointmentForm({ shouldClearSourceId: true });
    closeModal();
  };

  const selectPerson = (person: PersonTypes.Person) => {
    setPersonValue({
      firstName: person.FirstName,
      lastName: person.LastName,
      personId: person.PersonID,
      email: person.Email,
      mobilePhone: person.MobilePhone,
      homePhone: person.HomePhone,
      sourceId: person.SourceID,
      preferredName: person.PreferredName ?? '',
      pmId: person.PMID ?? '',
      pmDisplayId: person.PMDisplayID ?? '',
      gender: person.Gender,
      status: person.Status,
      birthDate: person.Birthdate,
    });
  };

  const submitAppointmentForm = async () => {
    if (!isFormValid) {
      validateAppointmentForm();
      setShouldShowFormErrors(true);
      return;
    }

    setIsSavingAppointment(true);
    const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);

    const appointmentDuration = getAppointmentDuration();

    const provider = providersList.find((provider) => provider.id === providerDropdownListSelectorProps.value);
    const practitionerName = `${provider?.firstName} ${provider?.lastName}`;

    try {
      const startDate = getAppointmentStartDate();
      const appointmentData: OnCreateCalendarEvent = {
        duration: appointmentDuration,
        locationId: selectedLocationId,
        personId: selectedPatientOption?.id || '',
        practitionerId: providerDropdownListSelectorProps.value,
        startDate,
        type: values.appointmentType || '',
        backgroundColor: values.backgroundColor || EVENT_BACKGROUND_COLOR,
      };

      if (configuredCalendarEvent?.eventId) {
        await onUpdateAppointment(
          {
            id: configuredCalendarEvent?.eventId,
            referenceId: configuredCalendarEvent?.referenceId,
            practitionerCalendarId: provider?.parentProviderId ?? '',
            person: personValue,
            appointmentTypeId: appointmentType?.id || '',
            appointmentTypeName: appointmentType?.name || '',
            practitionerName,
            ...appointmentData,
          },
          values.appointmentStatus as EventStatus
        );
        alerts.success(t('Successfully updated appointment'));
      } else {
        const provider = providersList.find((provider) => provider.id === providerDropdownListSelectorProps.value);
        await onCreateAppointment(
          {
            ...appointmentData,
            practitionerName,
            person: personValue,
            practitionerCalendarId: provider?.parentProviderId ?? '',
            sourceId: values?.source || singleSourceId,
            appointmentTypeId: values.appointmentType || '',
            appointmentTypeName: appointmentType?.name || '',
          },
          values.appointmentStatus as EventStatus
        );
        alerts.success(t('Successfully created appointment'));
      }
      setConfiguredCalendarEvent(null);
      // Use scheduler V3 invalidation to ensure fresh calendar event data
      invalidateListCalendarEvents({
        locationId: selectedLocationId,
        ignoreDate: true,
      });
      setSelectedPatientOption(undefined);
      setSelectedPerson(null);
      closeModal();
    } catch (error: any) {
      console.error(error);
      const errorMessage = configuredCalendarEvent?.eventId
        ? 'Failed to update appointment'
        : 'Failed to create appointment';
      alerts.error(errorMessage);
    } finally {
      setIsSavingAppointment(false);
    }
  };

  const customProviderList = useMemo(() => {
    if (values.appointmentType) {
      const availableProvidersByAppointmentTypes = appointmentTypes
        ?.find((appointmentType) => appointmentType.id === values.appointmentType)
        ?.providerAssets?.filter((provider) => provider.available);

      // Display providers who match the selected source id (if a provider has the source id property) and are available for the selected appointment type
      return providersList.filter(({ id, sourceId }) => {
        const isValidUUID = isUUID(id ?? '');
        if (!isValidUUID) return false;

        if (!!sourceId) {
          return availableProvidersByAppointmentTypes?.some(
            (providerAssetId) => providerAssetId.id === id && values.source === sourceId
          );
        } else {
          return availableProvidersByAppointmentTypes?.some((providerAssetId) => providerAssetId.id === id);
        }
      });
    }
    return [];
  }, [values.appointmentType, providersList]);

  useEffect(() => {
    const hasFieldValues = !!(
      providerDropdownListSelectorProps.value &&
      values.appointmentType &&
      values.appointmentDate &&
      values.locationId
    );

    const duration = appointmentTypes?.find((type) => type.id === values.appointmentType)?.durationMinutes;

    if (hasFieldValues && duration) {
      const provider = providersList.find((provider) => provider.id === providerDropdownListSelectorProps.value);

      setSelectedFormValuesForAvailabilityApi({
        appointmentDate: values.appointmentDate ?? '',
        appointmentType: values.appointmentType ?? '',
        selectedLocationId: values.locationId ?? '',
        selectedPractitionerId: providerDropdownListSelectorProps.value,
        selectedPractitionerCalendarId: provider?.parentProviderId ?? '',
        duration,
      });
    }
  }, [providerDropdownListSelectorProps.value, values.appointmentType, values.appointmentDate, values.locationId]);

  const getAppointmentStartDate = () => {
    if (!values.appointmentStartTime || !values.appointmentDate || !values.locationId) {
      return '';
    }
    return isScheduleV3
      ? formatLocationTime(`${values.appointmentDate} ${values.appointmentStartTime}`, values.locationId, {
          toTimezone: 'UTC',
        })
      : dayjs(`${values.appointmentDate} ${values.appointmentStartTime}`)?.toISOString();
  };

  const getAppointmentDuration = () => {
    if (!values.appointmentStartTime || !values.appointmentEndTime || !values.appointmentDate) {
      return 0;
    }
    if (values.appointmentStartTime && !values.appointmentEndTime && values.appointmentType) {
      const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);
      return appointmentType?.durationMinutes ?? 0;
    }

    return getTimeDiff(values.appointmentStartTime, values.appointmentEndTime, { crossDay: true });
  };

  // for initial mount case, set customDuration and appointmentEndTime
  useEffect(() => {
    const startHour = configuredCalendarEvent?.startHour ?? '';
    const endHour = configuredCalendarEvent?.endHour ?? '';
    const isEndTimeSelected = !!endHour && startHour !== endHour;

    const customDuration = getTimeDiff(startHour, endHour, { format: 'hh:mm A', crossDay: true });
    setCustomDuration(isEndTimeSelected ? customDuration : 0);

    // For new event case, set appointmentEndTime based on customDuration
    if (!configuredCalendarEvent?.eventId && startHour) {
      const endTime = dayjs(startHour, 'hh:mm A')
        .add(isEndTimeSelected ? customDuration || MIN_APPT_DURATION : MIN_APPT_DURATION, 'minute') // keep min 30 mins when empty
        .format('h:mma');
      seedValues({ appointmentEndTime: endTime });
    }
  }, [configuredCalendarEvent, seedValues]);

  const appointmentTypeDuration = useMemo(() => {
    const appointmentType = appointmentTypes?.find((type) => type.id === values.appointmentType);
    return appointmentType?.durationMinutes ?? 0;
  }, [appointmentTypes, values.appointmentType]);

  const timeSelectionHelperText = () => {
    if (
      values.appointmentStartTime ||
      values.appointmentEndTime ||
      !values.locationId ||
      !values.appointmentType ||
      !values.appointmentDate
    ) {
      return null;
    }

    return (
      <Chip variant='disabled' css={{ maxWidth: 'max-content' }}>
        {t('No Available Times')}
      </Chip>
    );
  };

  const handleDeleteAppointmentEventTypeSelection = () => {
    closeModal();
    onDeleteAppointment?.({
      id: configuredCalendarEvent?.eventId || '',
      createdByLocationId: configuredCalendarEvent?.locationId || '',
    });
    refetchAppointments?.(configuredCalendarEvent?.locationId);
  };

  const isEndTimeNextDay = useMemo(() => {
    const startTime = dayjs(`${values.appointmentDate} ${values.appointmentStartTime}`);
    const endTime = dayjs(`${values.appointmentDate} ${values.appointmentEndTime}`);

    if (!endTime.isAfter(startTime) && values.appointmentEndTime !== '00:00:00') {
      // If end time is not after start time, it is considered next day
      return true;
    }
    return false;
  }, [values.appointmentDate, values.appointmentStartTime, values.appointmentEndTime]);

  const nextDay = dayjs(values.appointmentDate).add(1, 'day').format('MMM Do');

  const hasFormTimeInputValues = !!values.appointmentStartTime && values.appointmentEndTime;

  const isFormValid =
    isAppointmentFormComplete &&
    providerDropdownListSelectorProps.value &&
    selectedPatientOption?.id &&
    (isScheduleV3 || !isEndTimeNextDay);

  const { pastDateBannerComponent } = usePastDateBanner({
    initialDate: configuredCalendarEvent?.eventId ? configuredCalendarEvent?.calendarDateValue : '',
    currentDate: values.appointmentDate,
    bannerMessage: t(
      "Heads up! This appointment is scheduled in the past. If that's intentional, go ahead and click Save."
    ),
    locationId: values.locationId ?? '',
  });

  const appointmentStatusProps = getFieldProps('appointmentStatus');

  const handleStatusChange = (appointmentStatus: EventStatus) => {
    appointmentStatusProps.onChange({ name: 'appointmentStatus', value: appointmentStatus });
  };

  const shouldShowRecommendedDurationBanner =
    !configuredCalendarEvent?.eventId &&
    appointmentTypeDuration !== customDuration &&
    !!values.appointmentStartTime &&
    !!values.appointmentEndTime;

  return (
    <div css={newAppointmentFormContainerStyles}>
      <div css={{ marginTop: theme.spacing(1), flexGrow: 1, overflowY: 'auto' }}>
        <Text size='small' color='light'>
          {!!configuredCalendarEvent?.eventId ? t('Manage Appointment') : t('New Appointment')}
        </Text>
        {selectedLocationIds.length > 1 && (
          <div css={fieldContainerStyles}>
            <Text>{t('Location')}</Text>
            <DropdownField
              disabled={isEditMode}
              label={t('{{count}} Locations', { count: selectedLocationIds.length })}
              css={{ input: { cursor: isEditMode ? 'not-allowed' : 'default' } }}
              {...getFieldProps('locationId')}
            >
              {selectedLocationIds.map((locationId) => {
                const locationName = getScopeName(locationId);
                return (
                  <DropdownField.Option key={locationId} value={locationId} searchValue={locationName ?? ''}>
                    <div
                      css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}
                      onClick={() => clearAppointmentForm({ shouldClearSourceId: false })}
                    >
                      <Icon name='location' size={16} color='light' />
                      <Text color={isEditMode ? 'disabled' : 'default'}>{locationName}</Text>
                    </div>
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </div>
        )}
        <div css={fieldContainerStyles}>
          <Text>{t('Patient')}</Text>
          <MultiViewCombobox
            showAvatar
            disabled={isEditMode}
            error={shouldShowFormErrors && !selectedPatientOption ? t('Patient is required') : ''}
            searchPlaceholder={t('Search Contacts')}
            value={selectedPatientOption ? [selectedPatientOption] : []}
            defaultViewConfig={defaultViewConfig}
            primaryViewConfig={primaryViewConfig}
            onChange={onPatientChange}
            onRemove={onPatientRemove}
            onSearch={onSearchChange}
          />
        </div>
        {isStatusDropdownVisible && (
          <div css={fieldContainerStyles}>
            <Text>{t('Status')}</Text>
            <AppointmentStatusComponents.EditableStatusChip
              status={appointmentStatusProps.value}
              onStatusChange={handleStatusChange}
            />
          </div>
        )}
        <div css={fieldContainerStyles}>
          <Text>{t('Type')}</Text>
          <DropdownField label={t('Select Type')} {...getFieldProps('appointmentType')}>
            <SearchField
              {...searchFieldProps}
              css={{ margin: theme.spacing(0.5) }}
              name='dropdown-search-appointment-type'
              autoFocus
              clearable
            />
            {filteredAppointmentTypes?.map((type) => {
              return (
                <DropdownField.Option key={type.id} value={type.id || ''}>
                  {type.name}
                </DropdownField.Option>
              );
            })}
          </DropdownField>
        </div>
        {canUseAppointmentColors && (
          <div css={[fieldContainerStyles, { gridTemplateColumns: 'minmax(auto, 35%) minmax(auto, 56px)' }]}>
            <Text>{t('Event Color')}</Text>
            <ColorPickerModal
              selectedColor={values.backgroundColor ?? EVENT_BACKGROUND_COLOR}
              palette={EVENT_BACKGROUND_COLOR_PALETTE}
              closeOnSelect={true}
              onChange={(color) => getFieldProps('backgroundColor').onChange({ value: color, name: 'backgroundColor' })}
            />
          </div>
        )}
        {shouldRenderSourceDropdown && (
          <div css={fieldContainerStyles}>
            <Text>{t('Source')}</Text>
            <DropdownField
              label={t('Select Source')}
              disabled={!!configuredCalendarEvent?.eventId}
              {...getFieldProps('source')}
            >
              {integrations?.map((source) => {
                return (
                  <DropdownField.Option key={source.SourceID} value={source.SourceID}>
                    {source.SourceName}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </div>
        )}
        <div css={providerFieldContainerStyles}>
          <Text>{t('Provider')}</Text>
          <ProviderDropdownListSelector
            providerDropdownListSelectorProps={providerDropdownListSelectorProps}
            listWidth={isMobile ? PROVIDER_SELECTOR_MOBILE_WIDTH : PROVIDER_SELECTOR_DESKTOP_WIDTH}
            css={{ padding: theme.spacing(1, 0) }}
            customProviderList={customProviderList}
            hideLocationTag={true}
            locationIdsToFilterProviders={values.locationId ? [values.locationId] : []}
            filterProviderByProviderUUID={true}
            providersList={providersList}
            selectedLocationIds={selectedLocationIds}
            shouldShowError={shouldShowFormErrors}
          />
        </div>
        <div css={fieldContainerStyles}>
          <Text>{t('Date')}</Text>
          <DatePickerField
            label={getLabelWithTimezoneAbbr(t('Date'), locationTimezoneAbbr)}
            {...getFieldProps('appointmentDate')}
          />
        </div>
        <div css={[fieldContainerStyles, timeFieldContainerStyles]}>
          <Text>{t('Start Time')}</Text>
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            {values.appointmentDate && (
              <TimeField
                {...getFieldProps('appointmentStartTime')}
                label={getLabelWithTimezoneAbbr(t('Start Time'), locationTimezoneAbbr)}
                showAdornment
                interval={5}
              />
            )}
          </div>
        </div>
        <div css={[fieldContainerStyles, timeFieldContainerStyles]}>
          <Text>{t('End Time')}</Text>
          {values.appointmentDate && (
            <>
              <TimeField
                {...getFieldProps('appointmentEndTime')}
                label={getLabelWithTimezoneAbbr(t('End Time'), locationTimezoneAbbr)}
                showAdornment
                interval={5}
                hasError={isEndTimeNextDay && !isScheduleV3}
              />
              {hasFormTimeInputValues && isEndTimeNextDay && (
                <Text
                  size='small'
                  css={{
                    color: isScheduleV3 ? theme.colors.indigo50 : theme.colors.critical50,
                    marginTop: theme.spacing(0.5),
                    gridArea: '2/2',
                  }}
                >
                  {isScheduleV3
                    ? t('Ends {{nextDay}} (next day)', { nextDay })
                    : t('End time must be after start time')}
                </Text>
              )}
            </>
          )}
        </div>
        <RecommendedDurationBanner
          hideBanner={!shouldShowRecommendedDurationBanner}
          appointmentTypeDuration={appointmentTypeDuration}
          selectedDuration={customDuration}
          onUpdate={(duration: number) => {
            setCustomDuration(0);
            seedValues({
              appointmentEndTime: dayjs(values.appointmentStartTime, 'HH:mm:ss')
                .add(duration, 'minute')
                .format('HH:mm:ss'),
            });
          }}
        />
        {!values.appointmentStartTime && !!providerDropdownListSelectorProps.value && !!values.appointmentType && (
          <div css={[fieldContainerStyles, timeFieldContainerStyles]}>
            <Text>{t('Available Times')}</Text>
            <div css={[availableTimeSlotContainer(!!values.appointmentStartTime)]}>
              {isAvailableAppointmentTimesLoading ? (
                <SpinningLoader size='small' css={{ maxWidth: 100 }} />
              ) : !availableAppointmentTimes?.length ? (
                timeSelectionHelperText()
              ) : (
                availableAppointmentTimes?.map((time) => (
                  <NakedButton
                    key={time.startTime}
                    onClick={() => {
                      const appointmentStartTime = formatDate(time.startTime, 'HH:mm:ss');
                      const endTime = updateEndTime(appointmentStartTime, values.appointmentType);
                      const appointmentEndTime = formatDate(endTime, 'HH:mm:ss');
                      seedValues({ appointmentStartTime, appointmentEndTime });
                    }}
                    css={[
                      timeButtonStyles,
                      values.appointmentStartTime === formatDate(time.startTime, 'HH:mm:ss') &&
                        selectedTimeButtonStyles,
                    ]}
                  >
                    {formatDate(time.startTime, 'h:mma')}
                  </NakedButton>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {pastDateBannerComponent}
      {!!configuredCalendarEvent?.eventId &&
        hasWSM &&
        (isScheduleV3 ? (
          <DeleteAppointmentButtonV3
            calendarEventId={configuredCalendarEvent.eventId}
            eventStartDate={configuredCalendarEvent.startDate}
            eventRefId={configuredCalendarEvent.referenceId}
            onSuccess={closeModal}
          />
        ) : (
          <DeleteAppointmentButton onDelete={handleDeleteAppointmentEventTypeSelection} />
        ))}
      <Tray.Actions
        primaryLabel={isSavingAppointment ? t('Saving...') : t('Save')}
        onPrimaryClick={submitAppointmentForm}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={() => cancelButtonHandler()}
        css={{ marginTop: 'auto' }}
        disabledPrimary={isSavingAppointment || isLoadingIntegrationDetails}
        primaryTrackingId={calendarEventsTrackingIds.saveAppointmentEventButton}
        secondaryTrackingId={calendarEventsTrackingIds.cancelAppointmentEventButton}
      />
    </div>
  );
};

const availableTimeSlotContainer = (isTimeSelected: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    maxHeight: '190px',
    overflowY: 'auto',
    ...(isTimeSelected && {
      filter: 'opacity(0.5)',
      transition: 'filter 0.2s ease-in-out',
      ':hover': { filter: 'opacity(1)' },
    }),
  });
const fieldContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(auto, 35%) minmax(auto, 280px)',
  alignItems: 'center',
  margin: theme.spacing(3, 0),
});

const providerFieldContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(auto, 35%) 1fr',
  alignItems: 'center',
  margin: theme.spacing(3, 0),
});

const timeFieldContainerStyles = css({
  alignItems: 'flex-start',
});

const timeButtonStyles = css({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.5),
  border: `1px solid ${theme.colors.neutral50}`,
  borderRadius: theme.borderRadius.small,
});

const selectedTimeButtonStyles = css({
  border: `1px solid ${theme.colors.primary50}`,
  backgroundColor: theme.colors.primary10,
  color: theme.colors.primary60,
});

const newAppointmentFormContainerStyles = () =>
  css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: theme.spacing(2),
  });
